import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Phone, Building2, Calendar, User, Copy, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export default function MessageDetailModal({ contact, open, onOpenChange }) {
  const [copied, setCopied] = useState(false);
  if (!contact) return null;

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copié");
    setTimeout(() => setCopied(false), 2000);
  };

  const rows = [
    { icon: User, label: "Nom", value: contact.name },
    { icon: Mail, label: "Email", value: contact.email, copyable: true },
    { icon: Phone, label: "Téléphone", value: contact.phone },
    { icon: Building2, label: "Entreprise", value: contact.company },
    { icon: Calendar, label: "Reçu le", value: new Date(contact.created_at).toLocaleString("fr-CH", { dateStyle: "full", timeStyle: "short" }) },
  ].filter((r) => r.value);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="message-detail-modal" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A1128]">
            <Mail size={18} className="text-[#00C853]" /> Message de {contact.name}
            {!contact.read && <span className="text-xs bg-[#00C853] text-white px-2 py-0.5 rounded-full ml-2">Nouveau</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Metadata */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-2.5">
            {rows.map((r, i) => {
              const Icon = r.icon;
              return (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <Icon size={14} className="text-slate-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-500">{r.label}</div>
                    <div className="text-slate-900 font-medium break-words">{r.value}</div>
                  </div>
                  {r.copyable && (
                    <button onClick={() => copy(r.value)} className="p-1.5 text-slate-400 hover:text-[#00C853] rounded transition-colors" title="Copier">
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Full message */}
          <div>
            <h4 className="text-sm font-semibold text-[#0A1128] mb-2">Message complet</h4>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed break-words">
                {contact.message}
              </p>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            {contact.email && (
              <a
                href={`mailto:${contact.email}?subject=Re: Votre demande AyaPay&body=Bonjour ${contact.name},%0A%0AMerci pour votre message.%0A%0A`}
                className="inline-flex items-center gap-1.5 bg-[#1B2B5B] hover:bg-[#0f1d3d] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                data-testid="reply-email-btn"
              >
                <Mail size={14} /> Répondre par email
              </a>
            )}
            {contact.phone && (
              <a
                href={`tel:${contact.phone.replace(/\s/g, "")}`}
                className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-[#00C853] hover:text-[#00C853] text-sm font-medium text-slate-700 px-4 py-2 rounded-lg transition-colors"
              >
                <Phone size={14} /> Appeler
              </a>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
